import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaBox, FaChartBar, FaMoneyBillWave, FaMobileAlt, FaWarehouse, FaChartLine, FaCheckCircle } from 'react-icons/fa';
import SEO from '../../components/common/SEO';

const featureIcons = {
  1: <FaBox />,
  2: <FaChartBar />,
  3: <FaMoneyBillWave />,
  4: <FaMobileAlt />,
  5: <FaWarehouse />,
  6: <FaChartLine />
};

const FoodIndustryPage = () => {
  const { t } = useTranslation();
  const { lang } = useParams();
  const industryKey = 'food';
  const gradient = '#dc2626 0%, #991b1b 100%';

  return (
    <>
      <SEO 
        titleKey={`industries.${industryKey}.seo.title`}
        descriptionKey={`industries.${industryKey}.seo.description`}
      />
      <PageContainer>
        <HeroSection $gradient={gradient}>
          <HeroContent>
            <HeroTag>{t(`industries.${industryKey}.hero.tag`)}</HeroTag>
            <HeroTitle>{t(`industries.${industryKey}.hero.title`)}</HeroTitle>
            <HeroSubtitle>{t(`industries.${industryKey}.hero.subtitle`)}</HeroSubtitle>
            <CTAButtons>
              <PrimaryButton to={`/${lang}/demo`} $mainColor={gradient.split(',')[1].trim().split(' ')[0]}>
                {t(`industries.${industryKey}.hero.ctaPrimary`)}
              </PrimaryButton>
              <SecondaryButton to={`/${lang}/contact`}>
                {t(`industries.${industryKey}.hero.ctaSecondary`)}
              </SecondaryButton>
            </CTAButtons>
          </HeroContent>
        </HeroSection>

        <FeaturesSection>
          <SectionTitle>{t(`industries.${industryKey}.features.title`)}</SectionTitle>
          <FeaturesGrid>
            {[1, 2, 3, 4, 5, 6].map((num) => (
              <FeatureCard key={num}>
                <FeatureIcon>{featureIcons[num]}</FeatureIcon>
                <FeatureTitle>{t(`industries.${industryKey}.features.items.feature${num}.title`)}</FeatureTitle>
                <FeatureDescription>
                  {t(`industries.${industryKey}.features.items.feature${num}.description`)}
                </FeatureDescription>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </FeaturesSection>

        <BarcodeScanningSection>
          <BarcodeContent>
            <BarcodeTextContent>
              <BarcodeTag>{t(`industries.${industryKey}.barcodeScanning.tag`)}</BarcodeTag>
              <BarcodeTitle>{t(`industries.${industryKey}.barcodeScanning.title`)}</BarcodeTitle>
              <BarcodeDescription>{t(`industries.${industryKey}.barcodeScanning.description`)}</BarcodeDescription>
              
              <BarcodeFeatures>
                {[1, 2, 3, 4, 5, 6].map((num) => (
                  <BarcodeFeature key={num}>
                    {t(`industries.${industryKey}.barcodeScanning.features.feature${num}`)}
                  </BarcodeFeature>
                ))}
              </BarcodeFeatures>
              
              <BarcodeHighlight>
                {t(`industries.${industryKey}.barcodeScanning.highlight`)}
              </BarcodeHighlight>
            </BarcodeTextContent>

            <BarcodeVisualContent>
              <BarcodePhoneMockup>
                <PhoneFrame />
                <PhoneScreen>
                  <ScannerArea>
                    <ScannerFrame>
                      <ScannerCorner position="top-left" />
                      <ScannerCorner position="top-right" />
                      <ScannerCorner position="bottom-left" />
                      <ScannerCorner position="bottom-right" />
                      <BarcodeInFrame>
                        <BarcodeStripes>
                          {[...Array(20)].map((_, i) => (
                            <BarcodeStripe key={i} width={Math.random() > 0.5 ? '3px' : '6px'} />
                          ))}
                        </BarcodeStripes>
                        <BarcodeNumber>8 690504 123456</BarcodeNumber>
                      </BarcodeInFrame>
                      <ScanLine />
                    </ScannerFrame>
                    <ScannerText>{t(`industries.${industryKey}.barcodeScanning.scanPrompt`)}</ScannerText>
                  </ScannerArea>
                </PhoneScreen>
              </BarcodePhoneMockup>
            </BarcodeVisualContent>
          </BarcodeContent>
        </BarcodeScanningSection>

        <BenefitsSection>
          <SectionTitle>{t(`industries.${industryKey}.benefits.title`)}</SectionTitle>
          <BenefitsList>
            {Array.from({ length: 14 }, (_, i) => i + 1).map((num) => (
              <BenefitItem key={num}>
                <BenefitIcon $gradient={gradient}><FaCheckCircle /></BenefitIcon>
                <BenefitText>
                  <BenefitTitle>{t(`industries.${industryKey}.benefits.items.benefit${num}.title`)}</BenefitTitle>
                  <BenefitDescription>
                    {t(`industries.${industryKey}.benefits.items.benefit${num}.description`)}
                  </BenefitDescription>
                </BenefitText>
              </BenefitItem>
            ))}
          </BenefitsList>
        </BenefitsSection>

        <CTASection $gradient={gradient}>
          <CTAContent>
            <CTATitle>{t(`industries.${industryKey}.cta.title`)}</CTATitle>
            <CTADescription>{t(`industries.${industryKey}.cta.description`)}</CTADescription>
            <CTAButton to={`/${lang}/demo`} $mainColor={gradient.split(',')[1].trim().split(' ')[0]}>
              {t(`industries.${industryKey}.cta.button`)}
            </CTAButton>
          </CTAContent>
        </CTASection>
      </PageContainer>
    </>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  padding: 120px 5% 80px;
  color: white;
  text-align: center;

  @media (max-width: 968px) {
    padding: 100px 5% 60px;
  }
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeroTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  opacity: 0.95;
  margin-bottom: 2rem;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
`;

const PrimaryButton = styled(Link)`
  background-color: ${props => props.$mainColor || 'white'};
  color: white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

const SecondaryButton = styled(Link)`
  background-color: transparent;
  color: white;
  border: 2px solid white;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  display: inline-block;

  &:hover {
    background-color: white;
    color: #1a202c;
    transform: translateY(-2px);
  }
`;

const FeaturesSection = styled.section`
  padding: 80px 5%;
  background-color: #f8fafc;
`;

const SectionTitle = styled.h2`
  text-align: center;
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 3rem;
  color: #1a202c;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.15);
  }
`;

const FeatureIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  text-align: center;
  color: #dc2626;

  svg {
    width: 3rem;
    height: 3rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #1a202c;
`;

const FeatureDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
`;

const scanAnimation = keyframes`
  0%, 100% { top: 10%; }
  50% { top: 80%; }
`;

const BarcodeScanningSection = styled.section`
  padding: 100px 5%;
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  color: white;
`;

const BarcodeContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const BarcodeTextContent = styled.div``;

const BarcodeTag = styled.span`
  display: inline-block;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
`;

const BarcodeTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BarcodeDescription = styled.p`
  font-size: 1.125rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  opacity: 0.95;
`;

const BarcodeFeatures = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BarcodeFeature = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 1rem;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 500;
`;

const BarcodeHighlight = styled.div`
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  padding: 1.5rem;
  border-radius: 12px;
  border-left: 4px solid #fca5a5;
  font-weight: 600;
  line-height: 1.6;
`;

const BarcodeVisualContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 968px) {
    order: -1;
  }
`;

const BarcodePhoneMockup = styled.div`
  position: relative;
  width: 300px;
  height: 600px;
`;

const PhoneFrame = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #1a202c;
  border-radius: 40px;
  padding: 15px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.3);

  &::before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    transform: translateX(-50%);
    width: 120px;
    height: 25px;
    background: #1a202c;
    border-radius: 0 0 20px 20px;
    z-index: 2;
  }
`;

const PhoneScreen = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #dc2626 0%, #991b1b 100%);
  border-radius: 30px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  padding: 2rem;
`;

const ScannerArea = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const ScannerFrame = styled.div`
  position: relative;
  width: 220px;
  height: 220px;
  border-radius: 20px;
`;

const ScannerCorner = styled.div`
  position: absolute;
  width: 30px;
  height: 30px;
  border: 3px solid #fca5a5;
  
  ${props => {
    switch(props.position) {
      case 'top-left':
        return 'top: 0; left: 0; border-right: none; border-bottom: none; border-radius: 20px 0 0 0;';
      case 'top-right':
        return 'top: 0; right: 0; border-left: none; border-bottom: none; border-radius: 0 20px 0 0;';
      case 'bottom-left':
        return 'bottom: 0; left: 0; border-right: none; border-top: none; border-radius: 0 0 0 20px;';
      case 'bottom-right':
        return 'bottom: 0; right: 0; border-left: none; border-top: none; border-radius: 0 0 20px 0;';
      default:
        return '';
    }
  }}
`;

const ScanLine = styled.div`
  position: absolute;
  left: 5%;
  right: 5%;
  height: 2px;
  background: linear-gradient(90deg, transparent, #fca5a5, transparent);
  animation: ${scanAnimation} 2s ease-in-out infinite;
  box-shadow: 0 0 10px #fca5a5;
`;

const ScannerText = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  text-align: center;
`;

const BarcodeInFrame = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const BarcodeStripes = styled.div`
  display: flex;
  gap: 2px;
  justify-content: center;
  margin-bottom: 0.5rem;
  height: 80px;
  align-items: center;
`;

const BarcodeStripe = styled.div`
  width: ${props => props.width};
  height: 100%;
  background: #1a202c;
`;

const BarcodeNumber = styled.div`
  text-align: center;
  font-family: monospace;
  font-size: 0.875rem;
  color: #1a202c;
  font-weight: 600;
`;

const BenefitsSection = styled.section`
  padding: 80px 5%;
  background-color: white;
`;

const BenefitsList = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: grid;
  gap: 1.5rem;
`;

const BenefitItem = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateX(10px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const BenefitIcon = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border-radius: 50%;
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.25rem;
`;

const BenefitText = styled.div`
  flex: 1;
`;

const BenefitTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const BenefitDescription = styled.p`
  color: #64748b;
  line-height: 1.6;
`;

const CTASection = styled.section`
  background: ${props => `linear-gradient(135deg, ${props.$gradient})`};
  padding: 80px 5%;
  color: white;
  text-align: center;
`;

const CTAContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CTADescription = styled.p`
  font-size: 1.25rem;
  margin-bottom: 2rem;
  opacity: 0.95;
  line-height: 1.6;
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background-color: ${props => props.$mainColor || 'white'};
  color: white;
  padding: 1rem 3rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.125rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
`;

export default FoodIndustryPage;
